/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */


const styles = css`
  :host {
    display: inline-block;
  }

  button {
    align-items: center;
    background: var(--primary, #18181b);
    background-clip: padding-box;
    border: 1px solid transparent;
    border-radius: var(--radius-md, 8px);
    box-sizing: border-box;
    color: var(--primary-foreground, #fafafa);
    cursor: pointer;
    display: flex;
    font: inherit;
    font-size: 0.875rem;
    font-weight: 500;
    gap: 8px;
    height: 36px;
    justify-content: center;
    line-height: 1;
    outline: none;
    padding: 0 16px;
    position: relative;
    transition:
      background-color 140ms ease,
      border-color 140ms ease,
      box-shadow 140ms ease,
      color 140ms ease,
      opacity 140ms ease,
      transform 80ms ease;
    user-select: none;
    white-space: nowrap;
    width: 100%;
  }

  button:hover {
    background: var(--primary-hover, #27272a);
  }

  button:focus-visible {
    border-color: var(--ring, #18181b);
    box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%));
  }

  button:active {
    transform: translateY(1px);
  }

  :host([variant="outline"]) button {
    background: var(--background, #ffffff);
    border-color: var(--border, #e4e4e7);
    color: var(--foreground, #18181b);
  }

  @media (prefers-color-scheme: dark) {
    :host([variant="outline"]) button { background: color-mix(in oklab, var(--input) 30%, transparent); }
    :host([variant="outline"]) button:hover { background: color-mix(in oklab, var(--input) 50%, transparent); }
  }

  :host([variant="outline"]) button:hover {
    background: var(--muted, #f4f4f5);
  }

  :host([variant="secondary"]) button {
    background: var(--secondary, #f4f4f5);
    color: var(--secondary-foreground, #18181b);
  }

  :host([variant="secondary"]) button:hover {
    background: var(--secondary-hover, #e4e4e7);
  }

  :host([variant="ghost"]) button {
    background: transparent;
    color: var(--foreground, #18181b);
  }

  :host([variant="ghost"]) button:hover {
    background: var(--muted, #f4f4f5);
  }

  :host([variant="destructive"]) button {
    background: var(--destructive-muted, rgb(220 38 38 / 10%));
    color: var(--destructive, #dc2626);
  }

  :host([variant="destructive"]) button:hover {
    background: var(--destructive-hover, rgb(220 38 38 / 20%));
  }

  :host([variant="destructive"]) button:focus-visible {
    border-color: var(--destructive-ring, rgb(220 38 38 / 40%));
    box-shadow: 0 0 0 3px var(--destructive-shadow, rgb(220 38 38 / 20%));
  }

  :host([variant="link"]) button {
    background: transparent;
    color: var(--primary, #18181b);
    height: auto;
    padding: 0;
    text-underline-offset: 4px;
  }

  :host([variant="link"]) button:hover {
    background: transparent;
    text-decoration: underline;
  }

  :host([size="xs"]) button {
    border-radius: 8px;
    font-size: 0.75rem;
    gap: 4px;
    height: 24px;
    padding: 0 8px;
  }

  :host([size="sm"]) button {
    border-radius: 8px;
    font-size: 0.8rem;
    gap: 4px;
    height: 32px;
    padding: 0 12px;
  }

  :host([size="lg"]) button {
    height: 40px;
    padding: 0 24px;
  }

  :host([size="icon"]) button {
    height: 36px;
    padding: 0;
    width: 36px;
  }

  :host([size="icon-xs"]) button {
    border-radius: 8px;
    height: 24px;
    padding: 0;
    width: 24px;
  }

  :host([size="icon-sm"]) button {
    border-radius: 8px;
    height: 32px;
    padding: 0;
    width: 32px;
  }

  :host([size="icon-lg"]) button {
    height: 40px;
    padding: 0;
    width: 40px;
  }

  :host([align="start"]) button { justify-content: flex-start; }

  :host([disabled]) button,
  :host([loading]) button {
    cursor: default;
    opacity: 0.5;
    pointer-events: none;
  }

  :host([disabled]) button:active,
  :host([loading]) button:active {
    transform: none;
  }

  .spinner {
    animation: spin 800ms linear infinite;
    border: 2px solid currentcolor;
    border-radius: 999px;
    border-right-color: transparent;
    display: none;
    height: 14px;
    width: 14px;
  }

  :host([loading]) .spinner {
    display: inline-block;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export class AppButton extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppButton)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["type","data-type"];

  #type = "button";
  #typeSet = false;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get type() {
    return this.#type;
  }

  set type(value) {
    const raw = value ?? "button";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#type, next)) return;
    this.#type = next;
    this.#typeSet = true;
    this.#updateBindings('type');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-type')) this.type = this.dataset.type;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.type = props.type;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#typeSet) this.type = this.dataset.type;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'type':
      case 'data-type':
        this.type = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><button type="button" data-argon="4567"><span class="spinner" aria-hidden="true"></span><slot></slot></button>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    

  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '4567').forEach(el => this.#listen(el, '4567', 'click', (event: Event) => {
    if (this.hasAttribute("disabled") || this.hasAttribute("loading")) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
    }
    if (this.#type === "submit") {
        event.preventDefault();
        this.closest("form")?.requestSubmit();
    }
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
